import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Typography, Button, Container, Paper } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null
		};
	}

	static getDerivedStateFromError(error: Error): State {
		// Update state so the next render will show the fallback UI
		return {
			hasError: true,
			error,
			errorInfo: null
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Log error to console in development
		if (process.env.NODE_ENV === "development") {
			console.error("Error caught by ErrorBoundary:", error, errorInfo);
		}

		// TODO: Log to error reporting service (e.g., Sentry, CloudWatch)
		// logErrorToService(error, errorInfo);

		this.setState({
			error,
			errorInfo
		});
	}

	handleReset = () => {
		this.setState({
			hasError: false,
			error: null,
			errorInfo: null
		});
		// Reload the page to reset the app state
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			// Custom fallback UI
			if (this.props.fallback) {
				return this.props.fallback;
			}

			// Default fallback UI
			return (
				<Container maxWidth="md">
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						minHeight="100vh"
						gap={3}
					>
						<Paper
							elevation={3}
							sx={{
								p: 4,
								textAlign: "center",
								maxWidth: 600
							}}
						>
							<ErrorOutlineIcon
								color="error"
								sx={{ fontSize: 80, mb: 2 }}
							/>

							<Typography variant="h4" gutterBottom color="error">
								Oops! Something went wrong
							</Typography>

							<Typography variant="body1" color="text.secondary" paragraph>
								We're sorry, but something unexpected happened. Please try
								refreshing the page.
							</Typography>

							{process.env.NODE_ENV === "development" && this.state.error && (
								<Box
									sx={{
										mt: 3,
										p: 2,
										backgroundColor: "#f5f5f5",
										borderRadius: 1,
										textAlign: "left",
										maxHeight: 200,
										overflow: "auto"
									}}
								>
									<Typography
										variant="body2"
										component="pre"
										sx={{
											fontFamily: "monospace",
											fontSize: "0.75rem",
											whiteSpace: "pre-wrap",
											wordBreak: "break-word"
										}}
									>
										{this.state.error.toString()}
										{this.state.errorInfo &&
											this.state.errorInfo.componentStack}
									</Typography>
								</Box>
							)}

							<Box mt={3} display="flex" gap={2} justifyContent="center">
								<Button
									variant="contained"
									color="primary"
									onClick={this.handleReset}
								>
									Refresh Page
								</Button>

								<Button
									variant="outlined"
									color="secondary"
									onClick={() => (window.location.href = "/home")}
								>
									Go to Home
								</Button>
							</Box>
						</Paper>
					</Box>
				</Container>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
